import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Branch } from '@tabify/models/types';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class MerchantService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api.baseUrl;

  /**
   * One-shot fetch of branch details. Use for the initial paint — for
   * live updates after that, use `streamBranch()`.
   * GET /api/v1/merchants/{merchantId}/branches/{branchId}
   */
  getBranch(merchantId: string, branchId: string): Observable<Branch> {
    return this.http.get<Branch>(
      `${this.baseUrl}/api/v1/merchants/${merchantId}/branches/${branchId}`,
    );
  }

  /**
   * Opens a Server-Sent Events stream that pushes a full Branch snapshot
   * whenever the underlying state changes (payment captured, POS poll
   * changed a table's status, etc.). Each emission is a FULL snapshot —
   * subscribers should REPLACE local state, not merge.
   *
   * Cold Observable: each subscribe opens a new EventSource; unsubscribe
   * closes it. EventSource auto-reconnects on transient errors (network
   * blips, 5xx, server redeploys) without erroring the Observable. For
   * terminal errors (4xx) the readyState becomes CLOSED and the Observable
   * errors so the subscriber can fall back or surface a toast.
   *
   * GET /api/v1/merchants/{merchantId}/branches/{branchId}/stream
   */
  streamBranch(merchantId: string, branchId: string): Observable<Branch> {
    const url = `${this.baseUrl}/api/v1/merchants/${merchantId}/branches/${branchId}/stream`;

    return new Observable<Branch>((subscriber) => {
      const es = new EventSource(url);

      es.onmessage = (event) => {
        try {
          const snapshot = JSON.parse(event.data) as Branch;
          subscriber.next(snapshot);
        } catch (err) {
          // Malformed frame — skip without tearing down; future frames may parse.
          console.error('Failed to parse merchant-branch SSE snapshot:', err);
        }
      };

      es.onerror = (event) => {
        // EventSource auto-reconnects on transient drops (its readyState goes
        // to CONNECTING). Only error the Observable when the connection is
        // terminally closed (4xx response — readyState CLOSED).
        if (es.readyState === EventSource.CLOSED) {
          subscriber.error(event);
        }
      };

      return () => es.close();
    });
  }
}
